import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, Box, Ruler, Info, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import useBoxTypes from '../../hooks/useBoxTypes';
import CallbackForm from '../ui/CallbackForm';

const API_BASE = process.env.REACT_APP_BACKEND_URL ?? '';
// Сроки аренды и поле скидки в тарифе (админка /admin → Тарифы)
const TERMS = [
  { m: 1, key: null },
  { m: 3, key: 'discount_3m_pct' },
  { m: 6, key: 'discount_6m_pct' },
  { m: 12, key: 'discount_12m_pct' },
];
const fmt = (n) => Number(n || 0).toLocaleString('ru-RU');

const BrutalistTariffs = () => {
  const { boxes: storageUnits } = useBoxTypes();
  const [selectedUnit, setSelectedUnit] = useState(() => storageUnits[2] || storageUnits[0]);
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [tariffs, setTariffs] = useState([]);
  // Пересинхронизируем выбор с актуальным списком (по code), чтобы не остался
  // устаревший объект из заглушки, пока грузится /api/box-types.
  useEffect(() => {
    if (!storageUnits.length) return;
    const fallback = storageUnits[2] || storageUnits[0];
    setSelectedUnit((prev) => storageUnits.find((u) => u.code === prev?.code) || fallback);
  }, [storageUnits]); // eslint-disable-line

  // Тарифы из админки — настоящие цены и скидки по срокам
  useEffect(() => {
    axios.get(`${API_BASE}/api/tariffs`).then((r) => setTariffs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // Тариф для типа бокса: по умолчанию, иначе самый дешёвый активный
  const chosenTariff = (code) => {
    const cands = tariffs.filter((t) => t.box_type_code === code);
    if (!cands.length) return null;
    return cands.find((t) => t.is_default) || [...cands].sort((a, b) => a.base_price_month - b.base_price_month)[0];
  };

  return (
    <section className="py-24 px-6 bg-background text-foreground min-h-screen">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 border-b-4 border-foreground pb-8"
        >
          <h1 className="text-5xl md:text-7xl font-sans font-bold uppercase tracking-tighter mb-4">
            ТАРИФЫ / БОКСЫ
          </h1>
          <p className="text-xl md:text-2xl font-mono text-muted-foreground uppercase tracking-widest">
            ВЫБЕРИТЕ СВОЙ РАЗМЕР
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Unit Selector List */}
          <div className="lg:col-span-4 space-y-4">
            {storageUnits.map((unit) => (
              <motion.div
                key={unit.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: unit.id * 0.1 }}
              >
                <button
                  onClick={() => setSelectedUnit(unit)}
                  className={`
                    w-full text-left p-6 border-2 transition-all duration-200 group relative overflow-hidden
                    ${selectedUnit.id === unit.id 
                      ? 'border-accent bg-accent text-accent-foreground shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] translate-x-2 -translate-y-2' 
                      : 'border-border hover:border-foreground bg-card hover:translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                    }
                  `}
                >
                  <div className="relative z-10 flex justify-between items-center">
                    <div>
                      <span className="font-mono text-sm uppercase tracking-widest opacity-70">Бокс {unit.name}</span>
                      <div className="text-3xl font-bold font-sans mt-1">{unit.size}</div>
                    </div>
                    {(() => {
                      const t = chosenTariff(unit.code);
                      const price = t ? t.base_price_month : unit.price;
                      return price ? (
                        <div className="text-right">
                          <div className="font-mono font-bold whitespace-nowrap">от {fmt(price)} ₽</div>
                          <div className="text-xs opacity-70">в месяц</div>
                        </div>
                      ) : (
                        <div className="text-right">
                          <div className="font-mono font-bold">Звоните</div>
                          <div className="text-xs opacity-70">узнать цену</div>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {unit.popular && (
                    <div className="absolute top-0 right-0 p-1">
                      <Sparkles className={`w-4 h-4 ${selectedUnit.id === unit.id ? 'text-white' : 'text-accent'}`} />
                    </div>
                  )}
                </button>
              </motion.div>
            ))}
          </div>

          {/* Unit Details */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedUnit.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="border-2 border-foreground bg-card p-8 md:p-12 relative shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]"
              >
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12 border-b-2 border-border pb-8">
                  <div>
                    <div className="flex items-center gap-4 mb-2">
                      <h2 className="text-4xl md:text-6xl font-bold font-sans uppercase">
                        {selectedUnit.name} • {selectedUnit.size}
                      </h2>
                      {selectedUnit.popular && (
                        <span className="bg-accent text-accent-foreground px-3 py-1 text-xs font-mono uppercase tracking-widest border border-accent-foreground">
                          Хит
                        </span>
                      )}
                    </div>
                    <p className="text-xl text-muted-foreground font-mono">
                      {selectedUnit.description}
                    </p>
                  </div>
                  
                  {(() => {
                    const t = chosenTariff(selectedUnit.code);
                    const base = t ? t.base_price_month : selectedUnit.price;
                    return base ? (
                      <div className="text-right bg-muted p-4 border border-border">
                        <div className="text-sm font-mono uppercase text-muted-foreground mb-1">Стоимость</div>
                        <div className="text-4xl font-bold font-sans text-accent">от {fmt(base)} ₽</div>
                        <div className="text-xs font-mono text-muted-foreground">в месяц</div>
                      </div>
                    ) : (
                      <div className="text-right bg-muted p-4 border border-border">
                        <div className="text-sm font-mono uppercase text-muted-foreground mb-1">Стоимость</div>
                        <a href="tel:+79184362255" className="text-4xl font-bold font-sans text-accent hover:underline block">
                          Звоните
                        </a>
                        <div className="text-xs font-mono text-muted-foreground">+7 918 436-22-55</div>
                      </div>
                    );
                  })()}
                </div>

                {/* Specs Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  <div className="border border-border p-6 bg-muted/30">
                    <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                      <Box className="w-5 h-5" />
                      <span className="font-mono text-sm uppercase">Объем</span>
                    </div>
                    <div className="text-3xl font-bold">{selectedUnit.volume}</div>
                  </div>
                  
                  <div className="border border-border p-6 bg-muted/30">
                    <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                      <Ruler className="w-5 h-5" />
                      <span className="font-mono text-sm uppercase">Габариты</span>
                    </div>
                    <div className="text-3xl font-bold">{selectedUnit.dimensions}</div>
                  </div>

                  <div className="border border-border p-6 bg-muted/30">
                    <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                      <Info className="w-5 h-5" />
                      <span className="font-mono text-sm uppercase">Статус</span>
                    </div>
                    <div className={`text-3xl font-bold ${selectedUnit.available ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedUnit.available ? 'СВОБОДЕН' : 'ЗАНЯТ'}
                    </div>
                  </div>
                </div>

                {/* Срок аренды и цена — из тарифов админки */}
                {(() => {
                  const t = chosenTariff(selectedUnit.code);
                  if (!t) return null;
                  const base = t.base_price_month;
                  return (
                    <div className="mb-12">
                      <h3 className="font-mono text-sm uppercase tracking-widest text-muted-foreground mb-6">
                        СРОК АРЕНДЫ И ЦЕНА
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {TERMS.map(({ m, key }) => {
                          const pct = key ? (t[key] || 0) : 0;
                          const price = Math.round(base * (1 - pct / 100));
                          return (
                            <div key={m} className="border-2 border-border p-4 bg-background text-center">
                              <div className="font-mono text-xs uppercase text-muted-foreground">{m} мес.</div>
                              <div className="text-2xl font-bold mt-1 whitespace-nowrap">{fmt(price)} ₽</div>
                              <div className="text-[11px] font-mono mt-1 h-4 text-accent">{pct > 0 ? `−${pct}%` : ''}</div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="font-mono text-[11px] text-muted-foreground mt-3">
                        Цена за месяц при оплате за выбранный период. Итоговая цена зависит от яруса бокса — выбирается при бронировании.
                      </p>
                    </div>
                  );
                })()}

                {/* Fits Section */}
                <div className="mb-12">
                  <h3 className="font-mono text-sm uppercase tracking-widest text-muted-foreground mb-6">
                    ЧТО ПОМЕСТИТСЯ ВНУТРИ
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {selectedUnit.fits.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-4 border border-border bg-background">
                        <div className="w-8 h-8 bg-accent/10 flex items-center justify-center border border-accent">
                          <Check className="w-4 h-4 text-accent" />
                        </div>
                        <span className="font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={() => setIsCallbackOpen(true)}
                    disabled={!selectedUnit.available}
                    className="flex-1 h-16 text-lg font-mono uppercase tracking-widest bg-foreground text-background hover:bg-accent hover:text-accent-foreground border-2 border-transparent hover:border-foreground transition-all duration-300"
                  >
                    {selectedUnit.available ? 'ЗАБРОНИРОВАТЬ СЕЙЧАС' : 'ОСТАВИТЬ ЗАЯВКУ'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      <CallbackForm
        isOpen={isCallbackOpen}
        onClose={() => setIsCallbackOpen(false)}
      />
    </section>
  );
};

export default BrutalistTariffs;
